import React, { useEffect, useMemo, useState } from "react";
import {
  Boxes, Plus, Minus, Trash2, Edit3, Save, X, AlertTriangle, Loader2, Search, Package,
} from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { money } from "../../lib/jobCalc";

const EMPTY = { part_code: "", description: "", supplier: "", cost: 0, qty_on_hand: 0, reorder_level: 1 };

export const AdminInventory = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showLowOnly, setShowLowOnly] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [newForm, setNewForm] = useState(EMPTY);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await adminApi.listInventory(false));
    } catch {
      toast.error("Could not load inventory");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (showLowOnly && it.qty_on_hand > it.reorder_level) return false;
      if (!q) return true;
      return (
        (it.part_code || "").toLowerCase().includes(q) ||
        (it.description || "").toLowerCase().includes(q) ||
        (it.supplier || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, showLowOnly]);

  const lowCount = items.filter((it) => it.qty_on_hand <= it.reorder_level).length;
  const totalValue = items.reduce((acc, it) => acc + (Number(it.cost) || 0) * (Number(it.qty_on_hand) || 0), 0);

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditForm({
      part_code: item.part_code || "",
      description: item.description || "",
      supplier: item.supplier || "",
      cost: item.cost || 0,
      qty_on_hand: item.qty_on_hand || 0,
      reorder_level: item.reorder_level || 1,
    });
  };
  const saveEdit = async () => {
    if (!editForm.part_code.trim()) return toast.error("Part code is required");
    try {
      await adminApi.updateInventory(editingId, {
        ...editForm,
        cost: Number(editForm.cost) || 0,
        qty_on_hand: Number(editForm.qty_on_hand) || 0,
        reorder_level: Number(editForm.reorder_level) || 1,
      });
      toast.success("Item updated");
      setEditingId(null);
      load();
    } catch { toast.error("Update failed"); }
  };

  const adjust = async (id, delta) => {
    try {
      await adminApi.adjustInventory(id, delta);
      load();
    } catch { toast.error("Adjustment failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this inventory item?")) return;
    try {
      await adminApi.deleteInventory(id);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (!newForm.part_code.trim()) return toast.error("Part code is required");
    try {
      await adminApi.createInventory({
        ...newForm,
        cost: Number(newForm.cost) || 0,
        qty_on_hand: Number(newForm.qty_on_hand) || 0,
        reorder_level: Number(newForm.reorder_level) || 1,
      });
      toast.success("Item added");
      setNewForm(EMPTY);
      setCreating(false);
      load();
    } catch { toast.error("Could not create item"); }
  };

  return (
    <div data-testid="admin-inventory-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display font-black uppercase text-3xl sm:text-4xl text-[#111619]">Inventory</h1>
          <p className="text-[#3f4b52] mt-1">Track windscreens and parts on hand. Items at or below reorder level are flagged.</p>
        </div>
        <button
          onClick={() => setCreating((c) => !c)}
          data-testid="inventory-new-btn"
          className="bg-[#f28a16] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2 hover:brightness-110"
        >
          {creating ? <X size={14} /> : <Plus size={14} />}
          {creating ? "Cancel" : "Add item"}
        </button>
      </div>

      {/* Summary */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard icon={Package} label="Distinct items" value={items.length} color="#20282c" />
        <SummaryCard icon={Boxes} label="Units in stock" value={items.reduce((a, i) => a + (i.qty_on_hand || 0), 0)} color="#3b82f6" />
        <SummaryCard icon={AlertTriangle} label="Low stock items" value={lowCount} color={lowCount > 0 ? "#dc2626" : "#10b981"} />
        <SummaryCard icon={Boxes} label="Stock value (cost)" value={money(totalValue)} color="#f28a16" />
      </div>

      {creating && (
        <form onSubmit={submitNew} className="mt-6 bg-white border border-[#cfd7db] rounded-md p-4" data-testid="inventory-create-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <Field label="Part code *"><Input value={newForm.part_code} onChange={(e) => setNewForm({ ...newForm, part_code: e.target.value })} data-testid="new-part-code" className="bg-white border-[#cfd7db] h-10" /></Field>
            <Field label="Description"><Input value={newForm.description} onChange={(e) => setNewForm({ ...newForm, description: e.target.value })} className="bg-white border-[#cfd7db] h-10" /></Field>
            <Field label="Supplier"><Input value={newForm.supplier} onChange={(e) => setNewForm({ ...newForm, supplier: e.target.value })} className="bg-white border-[#cfd7db] h-10" /></Field>
            <Field label="Cost (ZAR)"><Input type="number" step="0.01" value={newForm.cost} onChange={(e) => setNewForm({ ...newForm, cost: e.target.value })} className="bg-white border-[#cfd7db] h-10" /></Field>
            <Field label="Qty on hand"><Input type="number" value={newForm.qty_on_hand} onChange={(e) => setNewForm({ ...newForm, qty_on_hand: e.target.value })} data-testid="new-qty" className="bg-white border-[#cfd7db] h-10" /></Field>
            <Field label="Reorder level"><Input type="number" value={newForm.reorder_level} onChange={(e) => setNewForm({ ...newForm, reorder_level: e.target.value })} className="bg-white border-[#cfd7db] h-10" /></Field>
          </div>
          <button type="submit" data-testid="inventory-create-submit" className="mt-4 bg-[#20282c] text-white px-4 h-10 rounded-md font-display uppercase tracking-widest text-xs inline-flex items-center gap-2">
            <Save size={14} /> Save item
          </button>
        </form>
      )}

      {/* Filters */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9aa6ad]" />
          <Input
            placeholder="Search by part code, description, supplier"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="inventory-search"
            className="pl-9 bg-white border-[#cfd7db] h-10"
          />
        </div>
        <label className="inline-flex items-center gap-2 text-sm text-[#3f4b52]">
          <input
            type="checkbox"
            checked={showLowOnly}
            onChange={(e) => setShowLowOnly(e.target.checked)}
            data-testid="inventory-low-only"
            className="h-4 w-4 accent-[#f28a16]"
          />
          Show low-stock only
        </label>
      </div>

      {/* Table */}
      <div className="mt-4 bg-white border border-[#cfd7db] rounded-md overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[#3f4b52] inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="animate-spin" size={16} /> Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#3f4b52] text-sm">No items match.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#edf2f4] text-[#3f4b52] uppercase tracking-widest text-xs">
                <tr>
                  <th className="text-left px-4 py-2.5">Part code</th>
                  <th className="text-left px-4 py-2.5">Description</th>
                  <th className="text-left px-4 py-2.5">Supplier</th>
                  <th className="text-right px-4 py-2.5">Cost</th>
                  <th className="text-right px-4 py-2.5">On hand</th>
                  <th className="text-right px-4 py-2.5">Reorder</th>
                  <th className="text-right px-4 py-2.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#eef2f4]">
                {filtered.map((it) => {
                  const isLow = it.qty_on_hand <= it.reorder_level;
                  const isEditing = editingId === it.id;
                  return (
                    <tr key={it.id} className={isLow ? "bg-[#fff1f1]" : ""} data-testid={`inv-row-${it.id}`}>
                      {isEditing ? (
                        <>
                          <td className="px-4 py-2"><Input value={editForm.part_code} onChange={(e) => setEditForm({ ...editForm, part_code: e.target.value })} className="h-9 bg-white border-[#cfd7db]" /></td>
                          <td className="px-4 py-2"><Input value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} className="h-9 bg-white border-[#cfd7db]" /></td>
                          <td className="px-4 py-2"><Input value={editForm.supplier} onChange={(e) => setEditForm({ ...editForm, supplier: e.target.value })} className="h-9 bg-white border-[#cfd7db]" /></td>
                          <td className="px-4 py-2 text-right"><Input type="number" step="0.01" value={editForm.cost} onChange={(e) => setEditForm({ ...editForm, cost: e.target.value })} className="h-9 bg-white border-[#cfd7db] text-right" /></td>
                          <td className="px-4 py-2 text-right"><Input type="number" value={editForm.qty_on_hand} onChange={(e) => setEditForm({ ...editForm, qty_on_hand: e.target.value })} className="h-9 bg-white border-[#cfd7db] text-right" /></td>
                          <td className="px-4 py-2 text-right"><Input type="number" value={editForm.reorder_level} onChange={(e) => setEditForm({ ...editForm, reorder_level: e.target.value })} className="h-9 bg-white border-[#cfd7db] text-right" /></td>
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-end gap-1.5">
                              <button onClick={saveEdit} className="bg-[#10b981] text-white h-8 px-2.5 rounded-md text-xs inline-flex items-center gap-1" data-testid={`inv-save-${it.id}`}>
                                <Save size={12} /> Save
                              </button>
                              <button onClick={() => setEditingId(null)} className="border border-[#cfd7db] h-8 px-2.5 rounded-md text-xs inline-flex items-center gap-1">
                                <X size={12} /> Cancel
                              </button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-3 font-mono text-[#111619] font-semibold">{it.part_code}</td>
                          <td className="px-4 py-3 text-[#3f4b52]">{it.description || "—"}</td>
                          <td className="px-4 py-3 text-[#3f4b52]">{it.supplier || "—"}</td>
                          <td className="px-4 py-3 text-right text-[#3f4b52]">{it.cost ? money(it.cost) : "—"}</td>
                          <td className={`px-4 py-3 text-right font-display font-black ${isLow ? "text-[#dc2626]" : "text-[#111619]"}`}>
                            <div className="inline-flex items-center gap-1.5">
                              <button onClick={() => adjust(it.id, -1)} title="Decrement" className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#cfd7db] hover:bg-[#edf2f4]" data-testid={`inv-dec-${it.id}`}>
                                <Minus size={12} />
                              </button>
                              <span className="min-w-[2ch] text-center">{it.qty_on_hand}</span>
                              <button onClick={() => adjust(it.id, 1)} title="Increment" className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#cfd7db] hover:bg-[#edf2f4]" data-testid={`inv-inc-${it.id}`}>
                                <Plus size={12} />
                              </button>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-[#3f4b52]">{it.reorder_level}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1.5">
                              {isLow && (
                                <span className="px-2 py-0.5 bg-[#dc2626] text-white rounded-sm text-xs font-display uppercase tracking-widest inline-flex items-center gap-1">
                                  <AlertTriangle size={10} /> Low
                                </span>
                              )}
                              <button onClick={() => startEdit(it)} className="h-8 px-2.5 rounded-md text-xs inline-flex items-center gap-1 border border-[#cfd7db] hover:bg-[#edf2f4]" data-testid={`inv-edit-${it.id}`}>
                                <Edit3 size={12} /> Edit
                              </button>
                              <button onClick={() => remove(it.id)} className="h-8 px-2.5 rounded-md text-xs inline-flex items-center gap-1 text-[#dc2626] hover:bg-[#fff1f1]">
                                <Trash2 size={12} />
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-display uppercase tracking-widest text-[#3f4b52] block mb-1">{label}</span>
    {children}
  </label>
);

const SummaryCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white border border-[#cfd7db] rounded-md p-4">
    <div className="flex items-center justify-between mb-1.5">
      <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">{label}</div>
      <div className="h-8 w-8 rounded-md inline-flex items-center justify-center" style={{ background: `${color}1a`, color }}>
        <Icon size={16} />
      </div>
    </div>
    <div className="font-display font-black text-2xl text-[#111619]">{value}</div>
  </div>
);

export default AdminInventory;
